import React, { useEffect, useState } from "react";
import {
  CheckCircle2, AlertTriangle, XCircle, Clock, Sparkles, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import api from "@/lib/api";

const STATUS = {
  ok: { label: "OK", icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", dot: "bg-emerald-600" },
  warning: { label: "Manuell check", icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", dot: "bg-amber-600" },
  fail: { label: "Åtgärd krävs", icon: XCircle, color: "text-red-700", bg: "bg-red-50", border: "border-red-200", dot: "bg-red-600" },
  pending: { label: "Väntar", icon: Clock, color: "text-zinc-500", bg: "bg-zinc-50", border: "border-zinc-200", dot: "bg-zinc-400" },
};

const OVERALL_STYLE = {
  ok: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900", icon: CheckCircle2 },
  warning: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", icon: AlertTriangle },
  fail: { bg: "bg-red-50", border: "border-red-300", text: "text-red-900", icon: XCircle },
};

/**
 * Auto-granskningsmatris för bygglovsärenden.
 * Ger handläggaren en "Clean Desk på 15 minuter"-översikt.
 */
export default function CaseReviewMatrix({ caseId }) {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crm/cases/${caseId}/review-matrix`);
      setMatrix(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseId]);

  if (loading && !matrix) {
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-6 text-sm text-zinc-500">
        <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Auto-granskar ärendet...
      </div>
    );
  }
  if (!matrix) return null;

  const OverallIcon = OVERALL_STYLE[matrix.overall]?.icon || Sparkles;
  const overallStyle = OVERALL_STYLE[matrix.overall] || OVERALL_STYLE.warning;

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="review-matrix">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Sparkles className="w-4 h-4 text-blue-700" /> Handläggarens auto-granskning
          </h3>
          <p className="mt-1 text-xs text-zinc-500 max-w-2xl">
            Motorn kontrollerar handlingar, detaljplan-matchning, fastighetsgräns och teknisk beskrivning.
            Målet: du fattar beslut, inte detektivarbete.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="review-refresh"
          className="text-xs px-3 py-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-60 flex items-center gap-1.5">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Räkna om
        </button>
      </div>

      {/* Overall verdict banner */}
      <div className={`flex items-start gap-3 p-4 border rounded-sm ${overallStyle.bg} ${overallStyle.border} mb-4`}>
        <OverallIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${overallStyle.text}`} />
        <div>
          <div className={`text-xs uppercase tracking-wider font-semibold ${overallStyle.text}`}>
            Övergripande bedömning
          </div>
          <div className={`text-lg font-medium mt-0.5 ${overallStyle.text}`} style={{ fontFamily: "Outfit" }}>
            {matrix.overall_label}
          </div>
          <div className="mt-1 text-xs text-zinc-600">
            {matrix.meta.fails} fel · {matrix.meta.warnings} varningar · {matrix.meta.pendings} väntar · {matrix.meta.uploaded_docs} handlingar
          </div>
        </div>
      </div>

      {/* Individual checks */}
      <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-200">
        {matrix.checks.map((c) => {
          const s = STATUS[c.status] || STATUS.pending;
          const Icon = s.icon;
          const open = !!expanded[c.id];
          return (
            <div key={c.id} data-testid={`check-${c.id}`}>
              <button onClick={() => setExpanded({ ...expanded, [c.id]: !open })}
                className="w-full flex items-start gap-3 p-4 text-left hover:bg-zinc-50 transition">
                <div className={`w-2 h-2 rounded-full ${s.dot} mt-2 flex-shrink-0`} />
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-900">{c.title}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm ${s.bg} ${s.color} border ${s.border}`}>
                      {s.label}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-600">{c.summary}</div>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
              </button>
              {open && (
                <div className="px-4 pb-4 text-xs">
                  <div className="pl-9 space-y-2">
                    {c.id === "handlingar" && (
                      <div className="grid grid-cols-2 gap-3">
                        <DetailBlock title="Uppladdade handlingar" items={c.details.matched.length ? c.details.matched : ["(inga matcher)"]} color="emerald" />
                        <DetailBlock title="Saknas" items={c.details.missing.length ? c.details.missing : ["(inga)"]} color="red" />
                        {c.details.checklist_total > 0 && (
                          <div className="col-span-2 pt-2 border-t border-zinc-100 text-zinc-600">
                            Regelmotor-checklista: <strong>{c.details.checklist_done}/{c.details.checklist_total}</strong> avbockade.
                          </div>
                        )}
                      </div>
                    )}
                    {c.id === "detaljplan" && c.details.permit_meta && (
                      <pre className="bg-zinc-50 border border-zinc-100 p-2 rounded-sm text-[11px] overflow-x-auto">
                        {JSON.stringify(c.details.permit_meta, null, 2)}
                      </pre>
                    )}
                    {c.id === "fastighetsgrans" && (
                      <div className="text-zinc-600">
                        {c.details.boundary_distance_m != null
                          ? <>Uppmätt: <strong>{c.details.boundary_distance_m} m</strong> från tomtgräns.</>
                          : <>Inga siffror angivna i beskrivningen. Öppna situationsplanen för validering.</>}
                      </div>
                    )}
                    {c.id === "teknisk_beskrivning" && (
                      <div className="text-zinc-600">
                        Längd: <strong>{c.details.description_length}</strong> tecken.
                        {c.details.description_length < 200 && <span className="text-amber-700 ml-2">Rekommendation: minst 200 tecken.</span>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function DetailBlock({ title, items, color }) {
  const colorMap = {
    emerald: "text-emerald-700",
    red: "text-red-700",
  };
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${colorMap[color]} mb-1`}>{title}</div>
      <ul className="space-y-0.5 text-zinc-700">
        {items.map((it, i) => <li key={i}>• {it}</li>)}
      </ul>
    </div>
  );
}
